'use client';

import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WizardStepIndicatorProps {
  currentStep: number;
}

const STEPS = [
  { key: 'basic-info', labelKey: 'stepBasicInfo' },
  { key: 'add-fields', labelKey: 'stepAddFields' },
  { key: 'preview', labelKey: 'stepPreview' }
] as const;

export function WizardStepIndicator({ currentStep }: WizardStepIndicatorProps) {
  const t = useTranslations('dynamicForms');

  return (
    <div className='mb-8'>
      <nav aria-label='Progress'>
        <ol className='flex items-center justify-center gap-2 sm:gap-4'>
          {STEPS.map((step, index) => {
            const isCompleted = currentStep > index;
            const isCurrent = currentStep === index;

            return (
              <li key={step.key} className='flex items-center gap-2 sm:gap-4'>
                <div className='flex items-center gap-2'>
                  <div
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium',
                      isCompleted && 'bg-primary text-primary-foreground',
                      isCurrent &&
                        !isCompleted &&
                        'bg-primary text-primary-foreground',
                      !isCurrent &&
                        !isCompleted &&
                        'bg-muted text-muted-foreground'
                    )}
                  >
                    {isCompleted ? <Check className='h-4 w-4' /> : index + 1}
                  </div>
                  <span
                    className={cn(
                      'hidden text-sm font-medium sm:inline',
                      isCurrent || isCompleted
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    )}
                  >
                    {t(step.labelKey)}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={cn(
                      'h-px w-8 sm:w-16',
                      isCompleted ? 'bg-primary' : 'bg-muted'
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}
